// @flow
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, MenuItem} from '@material-ui/core';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import CustomSelect from '../../../../../elements/CustomSelect/CustomSelect';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import DatePicker from '../../../../../elements/DatePicker/DatePicker';
import DownloadIcon from '../../../../../elements/icons/DownloadIcon';
import {colors} from '../../../../../assets/styles/colors';
import type {CustomerComplaintState} from '../../../../../entities/customer-complaints';

type ComplaintFilter = {
  state?: CustomerComplaintState,
  dateFrom?: string,
  dateTo?: string,
  search?: string,
};

type Props = {|
  filter: ComplaintFilter,
  onFilterChange: (filter: ComplaintFilter) => void,
  onExport: () => void,
  totalComplaints: number,
|};

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: colors.grey900,
  },
  exportButton: {
    // display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    display: 'none',
  },
  filtersRow: {
    // display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: colors.grey50,
    borderRadius: '8px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    display: 'none',
  },
  searchField: {
    minWidth: 250,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  selectField: {
    minWidth: 150,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  dateField: {
    minWidth: 150,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  clearButton: {
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
}));

export default function ComplaintsHeader(props: Props) {
  const {filter, onFilterChange, onExport, totalComplaints} = props;
  const classes = useStyles();

  const handleFilterChange = (field: string, value: any) => {
    onFilterChange({
      ...filter,
      [field]: value,
    });
  };

  const clearFilters = () => {
    onFilterChange({});
  };

  const hasActiveFilters = !!(
    filter.search ||
    filter.state ||
    filter.dateFrom ||
    filter.dateTo
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          Quejas y reclamos ({totalComplaints})
        </Typography>
        <CustomButton
          variant="outlined"
          className={classes.exportButton}
          onClick={onExport}
          startIcon={<DownloadIcon />}
        >
          Exportar
        </CustomButton>
      </div>

      <div className={classes.filtersRow}>
        <CustomTextField
          className={classes.searchField}
          label="Buscar"
          placeholder="Buscar por cliente, email, correlativo..."
          value={filter.search || ''}
          onChange={e => handleFilterChange('search', e.target.value)}
        />

        <CustomSelect
          className={classes.selectField}
          label="Estado"
          value={filter.state || ''}
          onChange={e => handleFilterChange('state', e.target.value || null)}
        >
          <MenuItem value="">Todos los estados</MenuItem>
          <MenuItem value="RECEIVED">Recibido</MenuItem>
          <MenuItem value="SOLVED">Resuelto</MenuItem>
        </CustomSelect>

        <DatePicker
          className={classes.dateField}
          label="Fecha desde"
          value={filter.dateFrom || null}
          handleChange={date => handleFilterChange('dateFrom', date)}
          clearable
        />

        <DatePicker
          className={classes.dateField}
          label="Fecha hasta"
          value={filter.dateTo || null}
          handleChange={date => handleFilterChange('dateTo', date)}
          clearable
        />

        {hasActiveFilters && (
          <CustomButton
            variant="text"
            color="secondary"
            className={classes.clearButton}
            onClick={clearFilters}
          >
            Limpiar filtros
          </CustomButton>
        )}
      </div>
    </div>
  );
}
